<template>
  <va-card title="Protocol for Relaxation">
    <p class="display-4">By Dr. Karen L. Overall</p>
    <p class="display-6">Clinical Behavioral Medicine for Small Animals, 1997.</p>
    <va-accordion>
      <va-collapse>
        <span slot="header">
          About the Protocol
        </span>
        <div slot="body">
          <div>
            This program is the foundation for all other behavior modification programs.
            Its purpose is to teach the dog to sit and stay while relaxing in a variety
            of circumstances. The circumstances change from very reassuring ones with you
            present to potentially more stressful ones when you are absent. The purpose of
            the program is not to teach the dog to sit; sitting (or lying down, if the dog
            is more comfortable) is only a tool. The goals of the program are to teach the
            dog to relax, to defer to you, to enjoy earning a salary for an appropriate,
            desirable behavior, and to develop, as a foundation, a pattern of behaviors
            that allow the dog to cooperate with future behavior modification
            (generally desensitization and counter conditioning). This protocol acts as a
            foundation for teaching the dog context-specific appropriate behavior. The
            focus is to teach the dog to rely on you for all the cues as to the
            appropriateness of its behavior so that it can then learn not to react
            inappropriately.
          </div>
        </div>
      </va-collapse>
      <va-collapse>
        <span slot="header">
          About Food treats
        </span>
        <div slot="body">
          <span>
            This program uses food treats. Please read the logic behind this approach in the
            “Protocol for Deference: Basic Program." Remember, the treats are used as a
            salary or reward-not as a bribe.
            If you bribe a problem dog, you are defeated before you start. It is often
            difficult to work with a problem
            dog that has learned to manipulate bribes, but there are creative ways
            - often involving the use of head
            collars -to correct this situation. First, find a food that the dog
            likes and that it does not usually experience.
            Suggestions include boiled, slivered chicken or tiny pieces of cheese.
            Boiled, shredded chicken can be frozen
            in small portions and defrosted as needed. Individually wrapped slices
            of cheese can be divided into
            tiny pieces suitable for behavior modification while still wrapped in
            plastic, minimizing waste and mess.
            Consider the following guidelines in choosing a food reward:
            <ul>
              <li>Foods that are high in protein may help induce changes in brain
                chemistry that help the dog relax</li>
              <li>Dogs should not have chocolate because it can be toxic to them</li>
              <li>Some dogs do not do well with treats that contain artificial colors or preservatives</li>
              <li>Dogs with food allergies or those taking monoamine oxidase inhibitor (MAOI) drugs may have
                food restrictions (cheese, for dogs taking MAOIs [deprenyl])</li>
              <li>Dog biscuits generally are not sufficient motivation, but some foods
                are so desirable that the dog is too stimulated by them to relax
                - something between these two extremes is preferred</li>
              <li>Treats should be tiny (less than half the size of a thumbnail) so
                that the dog does not get full, fat, or bored</li>
              <li>If the dog stops responding for one kind of treat, try another</li>
              <li>Do not let treats make up the bulk of the dog's diet; the dog needs
                its normal, well-balanced ration</li>
            </ul>
          </span>
        </div>
      </va-collapse>
      <va-collapse>
        <span slot="header">
          The Reward Process
        </span>
        <div slot="body">
          <div>
            Rewarding dogs with food treats is an art. Learning to do so correctly
            helps the dog focus on
            the exercises and keeps everyone safe. To prevent the dog from lunging
            for the food, keep the already prepared treats in a little cup
            or plastic bag behind your back and keep one
            treat in the hand used to reward the dog.
            That hand can then either be kept behind your back
            so that the dog does not stare at the food or can be moved to
            your eye so that you can teach
            the dog to look happy and make eye contact with you.
            The food treat must be small so that the
            focus of the dog's attention is not a slab of food but rather your cues.
            A treat of the correct
            size can be closed in the palm of the hand by folding the fingers and
            will not be apparent when held between the thumb and forefingers.
            When presenting the dog with the treat, bring the
            hand, with a lightly closed fist, up quickly to the dog
            (do not startle the dog) and turn
            your wrist to open your hand.
          </div>
          <div class="mt-4">
            When starting the program, let the dog smell and taste the reward so that it knows the
            anticipated reward for the work. If the dog is too terrified to approach, you can place
            a small amount of the treat on the floor. Then ask the dog to "sit";
            if the dog sits instantly,
            say "Good girl (boy)!" and instantly open your hand to give the dog the
            treat instantly while saying "stay."
          </div>
          <div class="mt-4">
            Do not wave your hands or the treat around in front of the dog. This acts as a distraction
            and confuses the dog. Part of the point of this program is to make the dog calmer and less
            confused. Excitable behavior on your part or unclear signals can make your dog more anxious.
            This does not help.
          </div>
          <div class="mt-4">
            It is important to be calm. Your dog will make mistakes.
            This does not reflect on you.
            Problem dogs and new puppies require a lot of patience.
            The people who have had the most
            success with these protocols have been those who work
            the hardest and most consistently.
          </div>
        </div>
      </va-collapse>
      <va-collapse>
        <span slot="header">
          The Protocol
        </span>
        <div slot="body">
          <div>
            The protocol is a program that was designed so that your dog could
            learn from it without becoming stressed and without learning
            to ignore the tasks because they were too predictable.
            The protocol intersperses long activities with short ones.
            You may have to adjust some activities to your particular needs.
            The pattern is actually spelled out in the program. It is preferable
            to reward the dog only for performing each task perfectly.
            If this is not possible for your dog,
            you can use a "shaping" procedure in which you first reward
            the dog for a behavior that approaches
            that indicated in the task. The next time you do the task,
            the behavior must be closer to perfect
            to be rewarded. If the program is done correctly, your dog
            will perform the task perfectly within a short time.
          </div>
          <div class="mt-4">
            The protocol is a foundation for desensitizing and counterconditioning
            your dog to situations in which it reacts inappropriately.
            The pages can be used as one day's tasks, or
            you may proceed at the dog's
            pace (which may be faster or slower). Some exercises are weird (asking you
            to run in circles or talk
            to people who do not exist), but these can be very helpful in getting dogs
            to learn to relax in a variety of circumstances. Before you start the
            actual exercises, you must practice with the dog so that it
            can sit perfectly for 15 seconds without moving. Do this with food treats as
            described previously.
            Once your dog can sit this way and look happy and as if it worshipped
            the ground you walk on, you are ready for the more challenging stuff.
          </div>
          <div class="mt-4">
            Remember that the keys to success are consistency and appropriate rewards. This means that,
            although we want you to work 15 to 20 minutes once or twice per day, you should work only
            for as long as both you and the dog are enjoying and benefiting from the program. If this
            means that you use six 5- minute intervals to accomplish three or four of the tasks that is
            fine. Please do not end on a bad note. If the dog's behavior is deteriorating or its
            attention is dissipating, do one final, fun, easy exercise and stop. By pushing the dog
            past its limits, you induce anxiety, and the dog backslides.
          </div>
          <div class="mt-4">
            When the dog is able to perform all of the tasks and exercises both on- and off-lead in one
            location (the living room), repeat them all in other rooms and circumstances (the backyard
            or the park-use a lead here). When the dog performs all the tasks perfectly in all places
            with all household members, you are ready for Tier 2 of the protocols, which focuses on
            your dog's specific problems.
          </div>
          <div class="mt-4">
            If at any point you cannot get past one task, try breaking that task into two or three
            component parts. If this still does not help, call the veterinarian who recommended
            the program and who is working with the dog's behavior problems. He or she will be able
            to help you determine the root of the problem. Please do not just continue accepting
            suboptimal responses. The goal is to improve your dog's behavior. Videotaping while
            you work with the dog can help. Not only can you show the veterinarian what you are
            doing, but also you can be a more objective critic of your approach if you are not
            also an active participant.
          </div>
          <div class="mt-4">
            Finally, remember that the dog will give you lots of cues about how it feels.
            We are rewarding the physical changes associated with relaxation and happiness
            and so will also reward the underlying physiological states associated with this
            (parasympathetic part of the autonomic nervous system). This means that if the
            dog is relaxed, its body is not stiff, the jaws hang relaxed and are not tense,
            the ears are alert or cocked but not rigid, its head is held gently at an angle,
            and the eyes are calm and adoring, you will be rewarding the nervous system responses
            that help your dog learn. If you mistakenly reward fear, tension, aggression, or
            avoidance, you will not make as much progress. If it is easier for you and the dog
            to be relaxed if the dog is lying down, do that.
          </div>
          <div class="mt-4">
            Good luck, and do not get discouraged. Many dogs go through a period of 3 to 7 days
            when their behavior gets worse before it improves. For the first time in their life
            the dogs have a rule structure they must follow, and they get frustrated while learning
            it. As they discover they are rewarded for being relaxed and happy, their behavior will
            improve. These programs are more difficult for the people, in many ways, than they are
            for the dogs. Stick with it!
          </div>
        </div>
      </va-collapse>
    </va-accordion>
  </va-card>
</template>

<script>

export default {
  name: 'relaxation-protocol-background-card',
}
</script>

<style scoped lang="scss">
  .va-card {
    &__body {
      .va-accordion {
        margin-top: 1rem;
      }
    }
  }
</style>
